import { apiGet, APIURL, cancelSelectEvent, getCSSStyles, getEChartsTooltipTheme, getHeaderPinButtonHTML, getHeaderPinStateClass, getNoDataHTML, getWidgetDataURI, normalizeWidgetTheme, setupHeaderPin, toggleWidgetTheme } from '../../helpers';
import { SunburstMoverTypeDistributionConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

type SizeMetric = 'change' | 'volume' | 'price' | 'relative_volume' | 'market_cap';
type MoverTypeKey = 'Gainer' | 'Loser' | 'MostActive';

interface SunburstHierarchyNode {
    id: string;
    label: string;
    value: number;
    parent?: string;
}

interface SunburstApiData {
    parameters?: {
        size_metric?: string;
        top_n?: number;
    };
    summary_statistics?: Record<string, number>;
    sunburst_data?: {
        ids?: string[];
        labels?: string[];
        parents?: string[];
        values?: number[];
    };
    hierarchy_breakdown?: {
        parent_nodes?: SunburstHierarchyNode[];
        child_nodes?: SunburstHierarchyNode[];
    };
}

interface SunburstChartNode {
    name: string;
    value: number;
    rawValue: number;
    itemStyle?: { color: string };
    children?: SunburstChartNode[];
}

const MOVER_TYPE_LABELS: Record<MoverTypeKey, string> = {
    Gainer: 'Gainers',
    Loser: 'Losers',
    MostActive: 'Most Active'
};

const MOVER_TYPE_COLORS: Record<string, string> = {
    Gainer: '#FF1E1E',
    Gainers: '#FF1E1E',
    Loser: '#00C853',
    Losers: '#00C853',
    MostActive: '#1E5EFF',
    'Most Active': '#1E5EFF'
};

const MOVER_TYPE_LIGHT_COLORS: Record<string, string> = {
    Gainer: '#F5A3A3',
    Gainers: '#F5A3A3',
    Loser: '#8FE6B0',
    Losers: '#8FE6B0',
    MostActive: '#A8C5FF',
    'Most Active': '#A8C5FF'
};

function hexToRgb(hex: string): [number, number, number] {
    const normalized = hex.replace('#', '');
    const full =
        normalized.length === 3
            ? normalized
                  .split('')
                  .map((c) => c + c)
                  .join('')
            : normalized;
    return [
        parseInt(full.slice(0, 2), 16),
        parseInt(full.slice(2, 4), 16),
        parseInt(full.slice(4, 6), 16)
    ];
}

function rgbToHex(r: number, g: number, b: number): string {
    const toHex = (n: number) => Math.round(Math.max(0, Math.min(255, n))).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Blend base → light shade by t in [0, 1]. */
function blendHex(base: string, light: string, t: number): string {
    const [r1, g1, b1] = hexToRgb(base);
    const [r2, g2, b2] = hexToRgb(light);
    const clamped = Math.max(0, Math.min(1, t));
    return rgbToHex(
        r1 + (r2 - r1) * clamped,
        g1 + (g2 - g1) * clamped,
        b1 + (b2 - b1) * clamped
    );
}

function getMoverTypeColor(label: string): string {
    return MOVER_TYPE_COLORS[label] || '#8B6CFF';
}

function getMoverTypeLightColor(label: string): string {
    return MOVER_TYPE_LIGHT_COLORS[label] || '#C8B8FF';
}

/** Larger values stay closer to the parent color; smaller ones lighten. */
function applyChildShades(parentLabel: string, children: SunburstChartNode[]): void {
    if (!children.length) return;
    const base = getMoverTypeColor(parentLabel);
    const light = getMoverTypeLightColor(parentLabel);
    const maxVal = Math.max(...children.map((c) => c.value), 1);
    const minVal = Math.min(...children.map((c) => c.value));
    const range = Math.max(maxVal - minVal, 1e-9);

    children.forEach((child) => {
        const t = children.length === 1 ? 0 : (maxVal - child.value) / range;
        child.itemStyle = { color: blendHex(base, light, t * 0.85) };
    });
}

const METRIC_OPTIONS: Array<{ value: SizeMetric; label: string }> = [
    { value: 'change', label: 'Change' },
    { value: 'volume', label: 'Volume' },
    { value: 'price', label: 'Price' },
    { value: 'relative_volume', label: 'Relative Volume' },
    { value: 'market_cap', label: 'Market Cap' }
];

const TOP_N_OPTIONS = [5, 10, 15, 20];
const MOVER_TYPE_ORDER: MoverTypeKey[] = ['Gainer', 'Loser', 'MostActive'];

export class TraderverseSunburstMoverTypeDistribution {
    private container: HTMLElement;
    private config: SunburstMoverTypeDistributionConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private apiData: SunburstApiData | null = null;
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;
    private filters = {
        sizeMetric: 'change' as SizeMetric,
        topN: 5 as number | ''
    };

    constructor(config: SunburstMoverTypeDistributionConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.applyConfigFilters();
        this.container = this.resolveContainer(config.container);
        this.shadowRoot = (this.container as any).shadowRoot || this.container.attachShadow({ mode: 'closed' });
        this.init();
        this.widgetResizeObserver = new ResizeObserver(() => this.updateDisplay());
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private getDefaultConfig(): Partial<SunburstMoverTypeDistributionConfig> {
        return {
            headerPinned: true,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            sizeMetric: 'change',
            topN: 5
        };
    }

    private applyConfigFilters(): void {
        if (this.config.sizeMetric) this.filters.sizeMetric = this.config.sizeMetric as SizeMetric;
        if (typeof this.config.topN === 'number') this.filters.topN = this.config.topN;
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private async init(): Promise<void> {
        await this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.setupChart();
        if (widget) widget.classList.remove('loading');
        cancelSelectEvent(this.shadowRoot);
        this.emit('ready', {});
        this.config.onReady?.();
    }

    private async createWidget(): Promise<void> {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const metricOptions = (selected: string) =>
            METRIC_OPTIONS.map(
                ({ value, label }) =>
                    `<option value="${value}"${selected === value ? ' selected' : ''}>${label}</option>`
            ).join('');

        const topNOptions = TOP_N_OPTIONS.map(
            (n) => `<option value="${n}"${this.filters.topN === n ? ' selected' : ''}>${n}</option>`
        ).join('');

        const widgetHtml = `
        <div style="width: ${typeof this.config.width == 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
            <div class="headTop">
                <h4 class="title market">${this.getTitleIconSvg()} Sunburst Mover Type Distribution</h4>
                <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
                    <i class="pi pi-ellipsis-v"></i>
                </div>
            </div>
            <div class="inBody">
                <div class="optionsRow">
                    <div class="item">
                        <label>Size Metric</label>
                        <select class="filter-size-metric dropdown-btn">${metricOptions(this.filters.sizeMetric)}</select>
                    </div>
                    <div class="item">
                        <label>Top N</label>
                        <select class="filter-top-n dropdown-btn">${topNOptions}</select>
                    </div>
                </div>
                <div class="chartDv" id="sunburstMoverTypeContainer">
                    <div id="chartContainer" class="chart coverDv"></div>
                </div>
            </div>
        </div>`;

        this.shadowRoot.innerHTML = widgetHtml;
        await this.attachStyles();
        this.setupEventListeners();
    }

    private setupEventListeners(): void {
        setupHeaderPin(this.shadowRoot, this.config);

        const sizeMetricEl = this.shadowRoot.querySelector('.filter-size-metric') as HTMLSelectElement;
        const topNEl = this.shadowRoot.querySelector('.filter-top-n') as HTMLSelectElement;

        const updateFiltersAndFetch = () => {
            this.filters.sizeMetric = sizeMetricEl.value as SizeMetric;
            this.filters.topN = topNEl.value ? Number(topNEl.value) : 5;
            this.config.sizeMetric = this.filters.sizeMetric;
            this.config.topN = this.filters.topN;
            this.setupChart();
        };

        sizeMetricEl?.addEventListener('change', updateFiltersAndFetch);
        topNEl?.addEventListener('change', updateFiltersAndFetch);
    }

    private buildQueryParams(): Record<string, string | number> {
        const query: Record<string, string | number> = {
            sizeMetric: this.filters.sizeMetric
        };
        if (this.filters.topN !== '') query.topN = this.filters.topN;
        return query;
    }

    private formatMetricLabel(metric: string): string {
        const option = METRIC_OPTIONS.find((m) => m.value === metric);
        return option?.label || metric;
    }

    private formatMetricValue(value: number): string {
        if (!Number.isFinite(value)) return '-';
        return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }

    private getMoverTypeLabel(label: string): string {
        return MOVER_TYPE_LABELS[label as MoverTypeKey] || label;
    }

    private buildSunburstData(): SunburstChartNode[] {
        const breakdown = this.apiData?.hierarchy_breakdown;
        if (breakdown?.parent_nodes?.length) {
            const parentMap = new Map<string, SunburstChartNode>();

            const parentLabelById = new Map<string, string>();

            breakdown.parent_nodes.forEach((node) => {
                parentLabelById.set(node.id, node.label);
                parentMap.set(node.id, {
                    name: this.getMoverTypeLabel(node.label),
                    value: Math.abs(node.value),
                    rawValue: node.value,
                    itemStyle: { color: getMoverTypeColor(node.label) },
                    children: []
                });
            });

            (breakdown.child_nodes || []).forEach((node) => {
                const parent = parentMap.get(node.parent || '');
                if (!parent) return;
                parent.children!.push({
                    name: node.label,
                    value: Math.abs(node.value),
                    rawValue: node.value
                });
            });

            parentMap.forEach((parent, id) => {
                applyChildShades(parentLabelById.get(id) || parent.name, parent.children || []);
            });

            return MOVER_TYPE_ORDER
                .filter((type) => parentMap.has(type))
                .map((type) => parentMap.get(type)!)
                .concat(
                    Array.from(parentMap.entries())
                        .filter(([id]) => !MOVER_TYPE_ORDER.includes(id as MoverTypeKey))
                        .map(([, node]) => node)
                );
        }

        const sunburst = this.apiData?.sunburst_data;
        if (!sunburst?.ids?.length) return [];

        const parentMap = new Map<string, SunburstChartNode>();
        const parentLabelById = new Map<string, string>();

        sunburst.ids.forEach((id, index) => {
            const label = sunburst.labels?.[index] || id;
            const parentId = sunburst.parents?.[index] || '';
            const rawValue = Number(sunburst.values?.[index]) || 0;
            const node: SunburstChartNode = {
                name: parentId ? label : this.getMoverTypeLabel(label),
                value: Math.abs(rawValue),
                rawValue,
                ...(parentId ? {} : { itemStyle: { color: getMoverTypeColor(label) }, children: [] })
            };

            if (!parentId) {
                parentLabelById.set(id, label);
                parentMap.set(id, node);
                return;
            }

            const parent = parentMap.get(parentId);
            if (parent) parent.children!.push(node);
        });

        parentMap.forEach((parent, id) => {
            applyChildShades(parentLabelById.get(id) || parent.name, parent.children || []);
        });

        return MOVER_TYPE_ORDER
            .filter((type) => parentMap.has(type))
            .map((type) => parentMap.get(type)!)
            .concat(
                Array.from(parentMap.entries())
                    .filter(([id]) => !MOVER_TYPE_ORDER.includes(id as MoverTypeKey))
                    .map(([, node]) => node)
            );
    }

    private hasChartData(): boolean {
        return this.buildSunburstData().length > 0;
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private async setupChart(): Promise<void> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const containerWrapper = this.shadowRoot.querySelector('#sunburstMoverTypeContainer') as HTMLElement;
        if (widget) widget.classList.add('loading');

        try {
            if ((this.config as any).data) {
                this.apiData = (this.config as any).data as SunburstApiData;
            } else {
                const res = await apiGet({
                    method: 'GET',
                    url: `${APIURL}/v1/equity-market/sunburst-mover-type-distribution`,
                    query: this.buildQueryParams(),
                    headers: { }
                });

                if (!res.ok) throw new Error(`API error: ${res.status}`);
                const json = await res.json();
                if (!json?.isSuccess) throw new Error(json?.message || 'Failed to fetch sunburst mover type distribution');
                this.apiData = (json?.data ?? null) as SunburstApiData | null;
            }

            if (this.hasChartData()) {
                containerWrapper?.setAttribute('data-testing-id', 'sunburst-mover-type-chart');
            } else {
                containerWrapper?.removeAttribute('data-testing-id');
            }

            this.updateDisplay();
            this.emit('dataUpdate', this.apiData);
        } catch (err) {
            console.error('Error fetching sunburst mover type data:', err);
            this.apiData = null;
            containerWrapper?.removeAttribute('data-testing-id');
            this.updateDisplay();
            this.emit('error', err);
        } finally {
            if (widget) widget.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        if (!(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }
        this.drawChart();
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#chartContainer') as HTMLDivElement;
        const containerWrapper = this.shadowRoot.querySelector('#sunburstMoverTypeContainer') as HTMLElement;
        if (!container) return;

        if (this.currentChart) {
            this.currentChart.dispose();
            this.currentChart = null;
        }

        const chartData = this.buildSunburstData();
        if (!chartData.length) {
            container.innerHTML = getNoDataHTML();
            return;
        }

        container.innerHTML = '';

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color').trim() || '#ffffff';
        const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm');
        const bgColor = getComputedStyle(widgetBox).getPropertyValue('--base-dark');
        const tooltipTheme = getEChartsTooltipTheme(widgetBox);
        const metricLabel = this.formatMetricLabel(this.filters.sizeMetric);

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                ...tooltipTheme,
                trigger: 'item',
                formatter: (params: any) => {
                    const rawValue = params.data?.rawValue ?? params.value;
                    const formattedValue = this.formatMetricValue(Number(rawValue));
                    return `
                        <div style="min-width:140px">
                            <div style="font-weight:600;margin-bottom:6px">${params.name}</div>
                            <div>${metricLabel}: <b>${formattedValue}</b></div>
                        </div>`;
                }
            },
            series: [
                {
                    type: 'sunburst',
                    radius: ['12%', '90%'],
                    center: ['50%', '52%'],
                    sort: undefined,
                    emphasis: { focus: 'ancestor' },
                    nodeClick: 'rootToNode',
                    itemStyle: {
                        borderRadius: 6,
                        borderColor: bgColor,
                        borderWidth: 2
                    },
                    label: {
                        color: textColor,
                        fontSize,
                        rotate: 'radial',
                        minAngle: 8
                    },
                    levels: [
                        {},
                        {
                            r0: '12%',
                            r: '42%',
                            label: { fontWeight: 600 }
                        },
                        {
                            r0: '42%',
                            r: '90%',
                            label: { fontSize }
                        }
                    ],
                    data: chartData
                }
            ]
        };

        this.currentChart = echarts.init(container);
        this.currentChart.setOption(option);
        containerWrapper?.setAttribute('data-testing-id', 'sunburst-mover-type-chart');
    }

    public updateWithResponse(resp: SunburstApiData): void {
        if (!resp) return;
        this.apiData = resp;
        this.updateDisplay();
        this.emit('dataUpdate', resp);
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter((h) => h !== handler));
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite'): void {
        if (!this.shadowRoot) return;
        this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as
            | 'white'
            | 'black'
            | 'default-dark'
            | 'default-lite';
        this.updateDisplay();
    }

    public destroy(): void {
        if (this.currentChart) {
            this.currentChart.dispose();
            this.currentChart = null;
        }
        if (this.widgetResizeObserver) {
            this.widgetResizeObserver.disconnect();
            this.widgetResizeObserver = null;
        }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const evt: WidgetEvent = { type, data };
            handlers.forEach((h) => h(evt));
        }
    }

    private getTitleIconSvg(): string {
        return `<svg width="27" height="22" viewBox="0 0 27 22" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M22.6125 22H4.3875C3.26681 22.0001 2.18858 21.5634 1.37399 20.7794C0.559395 19.9955 0.0701982 18.9239 0.00675018 17.7843L2.29482e-08 17.5312V3.09375C-9.45295e-05 2.30983 0.291999 1.5551 0.817249 0.982089C1.3425 0.409082 2.06173 0.0605304 2.8296 0.00687518L3.0375 2.33732e-08H19.9125C20.6822 -9.62801e-05 21.4232 0.297406 21.9858 0.832383C22.5484 1.36736 22.8906 2.09991 22.9433 2.882L22.95 3.09375V4.125H23.9625C24.7322 4.1249 25.4732 4.42241 26.0358 4.95738C26.5984 5.49236 26.9406 6.22491 26.9933 7.007L27 7.21875V17.5312C27.0001 18.6727 26.5713 19.7709 25.8016 20.6006C25.032 21.4302 23.9798 21.9285 22.8609 21.9931L22.6125 22ZM4.3875 19.9375H22.6125C23.2056 19.9374 23.777 19.7102 24.2132 19.3008C24.6494 18.8914 24.9184 18.33 24.9669 17.7279L24.975 17.5312V7.21875C24.975 6.96955 24.8864 6.72878 24.7256 6.54097C24.5647 6.35316 24.3426 6.23102 24.1002 6.19712L23.9625 6.1875H22.95V16.8438C22.95 17.093 22.8614 17.3337 22.7006 17.5215C22.5397 17.7093 22.3176 17.8315 22.0752 17.8654L21.9375 17.875C21.6928 17.875 21.4564 17.7847 21.272 17.6209C21.0876 17.4571 20.9677 17.2309 20.9345 16.984L20.925 16.8438V3.09375C20.925 2.84455 20.8364 2.60378 20.6756 2.41597C20.5147 2.22816 20.2926 2.10602 20.0502 2.07212L19.9125 2.0625H3.0375C2.79283 2.06251 2.55644 2.15276 2.37204 2.31656C2.18765 2.48035 2.06773 2.70661 2.03445 2.9535L2.025 3.09375V17.5312C2.02502 18.1351 2.24798 18.7169 2.64962 19.1612C3.05125 19.6054 3.6022 19.8796 4.1931 19.9292L4.3875 19.9375ZM13.8321 14.4375H17.8902C18.1467 14.4376 18.3937 14.5368 18.5811 14.7152C18.7686 14.8936 18.8826 15.1378 18.9 15.3985C18.9175 15.6591 18.8372 15.9169 18.6753 16.1195C18.5134 16.3222 18.282 16.4548 18.0279 16.4904L17.8902 16.5H13.8321C13.5756 16.4999 13.3286 16.4007 13.1412 16.2223C12.9537 16.0439 12.8398 15.7997 12.8223 15.539C12.8048 15.2784 12.8851 15.0206 13.047 14.818C13.2089 14.6153 13.4403 14.4827 13.6944 14.4471L13.8321 14.4375ZM9.77805 9.63188C10.0466 9.63188 10.3041 9.74052 10.494 9.93392C10.6839 10.1273 10.7906 10.3896 10.7906 10.6631V15.4688C10.7906 15.7423 10.6839 16.0046 10.494 16.198C10.3041 16.3914 10.0466 16.5 9.77805 16.5H5.0598C4.79127 16.5 4.53373 16.3914 4.34385 16.198C4.15397 16.0046 4.0473 15.7423 4.0473 15.4688V10.6631C4.0473 10.3896 4.15397 10.1273 4.34385 9.93392C4.53373 9.74052 4.79127 9.63188 5.0598 9.63188H9.77805ZM8.76555 11.6944H6.0723V14.4375H8.76555V11.6944ZM13.8321 9.63188H17.8902C18.1467 9.63196 18.3937 9.73121 18.5811 9.90959C18.7686 10.088 18.8826 10.3322 18.9 10.5928C18.9175 10.8535 18.8372 11.1112 18.6753 11.3139C18.5134 11.5166 18.282 11.6491 18.0279 11.6848L17.8902 11.6944H13.8321C13.5737 11.6971 13.3242 11.599 13.1344 11.4204C12.9447 11.2417 12.8293 10.996 12.8117 10.7334C12.7941 10.4709 12.8757 10.2114 13.0398 10.0082C13.2039 9.8049 13.4381 9.67324 13.6944 9.64012L13.8321 9.63188ZM5.0598 4.81525H17.8902C18.1486 4.81257 18.3981 4.9106 18.5879 5.08925C18.7776 5.2679 18.893 5.51366 18.9106 5.7762C18.9282 6.03875 18.8466 6.29821 18.6825 6.50147C18.5184 6.70472 18.2842 6.83639 18.0279 6.8695L17.8902 6.87775H5.0598C4.80327 6.87767 4.55633 6.77841 4.36888 6.60004C4.18143 6.42166 4.06745 6.17746 4.04997 5.91679C4.03248 5.65612 4.1128 5.3984 4.27468 5.19571C4.43657 4.99303 4.66796 4.86049 4.9221 4.82487L5.0598 4.81525Z" fill="currentColor"/> </svg>`;
    }
}

declare global {
    interface Window {
        TraderverseSunburstMoverTypeDistribution: typeof TraderverseSunburstMoverTypeDistribution;
    }
}
if (typeof window !== 'undefined') {
    window.TraderverseSunburstMoverTypeDistribution = TraderverseSunburstMoverTypeDistribution;
}
